import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { api } from "../lib/api";
import { ArrowLeft, ArrowRight, Calendar, Clock, Loader2 } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function BlogArticle() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Sanitize the article HTML before rendering. Content is proxied from an
  // external CMS (BabyLoveGrowth) so we treat it as untrusted and strip any
  // scriptable payload before it ever reaches `dangerouslySetInnerHTML`.
  const safeHtml = useMemo(() => {
    if (!article?.content_html) return "";
    return DOMPurify.sanitize(article.content_html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
    });
  }, [article]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/blog/articles/${encodeURIComponent(id)}`);
        if (!cancelled) { setArticle(r.data.article); setRelated(r.data.related || []); }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 404 ? "not_found" : "load_failed");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  // JSON-LD Article schema — injected into <head> so Google can show the post as a
  // rich result. Removed on unmount so it doesn't leak into other routes.
  useEffect(() => {
    if (!article) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt || "",
      "image": article.cover_image ? [article.cover_image] : undefined,
      "datePublished": article.published_at || undefined,
      "dateModified": article.published_at || undefined,
      "author": [{ "@type": "Organization", "name": article.author || "The Extension Works" }],
      "publisher": {
        "@type": "Organization",
        "name": "The Extension Works",
        "logo": {
          "@type": "ImageObject",
          "url": `${window.location.origin}/favicon.ico`,
        },
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${window.location.origin}/blog/${article.id}`,
      },
      "keywords": Array.isArray(article.tags) ? article.tags.join(", ") : undefined,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-testid", "blog-article-jsonld");
    script.text = JSON.stringify(ld, (_k, v) => v === undefined ? undefined : v);
    document.head.appendChild(script);

    const prevTitle = document.title;
    document.title = `${article.title} — The Extension Works`;

    return () => {
      document.head.removeChild(script);
      document.title = prevTitle;
    };
  }, [article]);

  return (
    <main data-testid="blog-article-page" className="px-6 md:px-12 lg:px-16 pt-10 pb-24">
      <Link to="/blog" data-testid="blog-article-back" className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#C79A3B] mb-8"><ArrowLeft size={14}/> Back to all articles</Link>

      {loading && (
        <div className="flex items-center gap-2 text-[#6B7280] text-sm" data-testid="blog-article-loading"><Loader2 size={16} className="animate-spin"/> Loading article…</div>
      )}

      {!loading && err === "not_found" && (
        <div className="de-card p-12 max-w-2xl" data-testid="blog-article-404">
          <div className="label-eyebrow mb-3 text-[#C79A3B]">Article not found</div>
          <p className="text-lg mb-4">That article isn&apos;t available right now &mdash; it may have been updated or removed.</p>
          <Link to="/blog" className="de-btn-secondary">See all articles</Link>
        </div>
      )}

      {!loading && err && err !== "not_found" && (
        <div className="de-card p-12 max-w-2xl" data-testid="blog-article-error">
          <div className="label-eyebrow mb-3 text-[#C79A3B]">Something went wrong</div>
          <p className="text-lg mb-4">We couldn&apos;t load this article. Please try again in a moment.</p>
          <Link to="/blog" className="de-btn-secondary">Back to blog</Link>
        </div>
      )}

      {!loading && article && (
        <>
          <article className="max-w-3xl mx-auto" data-testid="blog-article-body">
            {(article.tags && article.tags.length > 0) && (
              <div className="label-eyebrow text-[#C79A3B] mb-4">{(article.tags[0] || "").toString()}</div>
            )}
            <h1 className="font-black tracking-tighter leading-[1.0] text-4xl md:text-6xl mb-6">{article.title}</h1>
            {article.excerpt && <p className="text-lg text-[#6B7280] mb-8 leading-relaxed">{article.excerpt}</p>}
            <div className="flex flex-wrap items-center gap-4 text-xs text-[#6B7280] border-t border-b border-[#E5E7EB] py-4 mb-10">
              <span className="inline-flex items-center gap-1.5"><b className="text-[#12263A]">{article.author || "The Extension Works"}</b></span>
              {article.published_at && <span className="inline-flex items-center gap-1"><Calendar size={12}/> {formatDate(article.published_at)}</span>}
              {article.reading_time_min && <span className="inline-flex items-center gap-1"><Clock size={12}/> {article.reading_time_min} min read</span>}
            </div>
            {article.cover_image && (
              <img src={article.cover_image} alt={article.title} className="w-full mb-10 aspect-[16/9] object-cover" data-testid="blog-article-cover"/>
            )}
            <div
              className="blog-prose max-w-none text-[#12263A] leading-[1.75] text-base md:text-lg"
              data-testid="blog-article-content"
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          </article>

          {related.length > 0 && (
            <aside className="max-w-6xl mx-auto mt-20 border-t border-[#E5E7EB] pt-12" data-testid="blog-related">
              <div className="label-eyebrow text-[#C79A3B] mb-6">Related reads</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map(r => (
                  <Link to={`/blog/${r.id}`} key={r.id} data-testid={`blog-related-${r.id}`}
                    className="group de-card overflow-hidden hover:border-[#C79A3B] transition-colors flex flex-col">
                    {r.cover_image
                      ? <div className="aspect-[16/9] overflow-hidden bg-[#F0EAE0]"><img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"/></div>
                      : <div className="aspect-[16/9] bg-[#12263A] flex items-center justify-center"><span className="text-[10px] uppercase tracking-[3px] text-[#C79A3B] font-bold">The Extension Works</span></div>}
                    <div className="p-5 flex flex-col flex-1">
                      {(r.tags && r.tags.length > 0) && (
                        <div className="label-eyebrow text-[#C79A3B] mb-2 truncate">{(r.tags[0] || "").toString()}</div>
                      )}
                      <h3 className="text-base font-bold tracking-tight mb-2 leading-snug line-clamp-3">{r.title}</h3>
                      {r.excerpt && <p className="text-xs text-[#6B7280] line-clamp-2">{r.excerpt}</p>}
                      <span className="mt-3 inline-flex items-center gap-1 text-[#C79A3B] font-bold text-xs group-hover:gap-2 transition-all">Read <ArrowRight size={12}/></span>
                    </div>
                  </Link>
                ))}
              </div>
            </aside>
          )}
        </>
      )}
    </main>
  );
}
